/**
 * Fetch wrapper: one place for the base URL, auth header, token refresh and
 * error normalisation. Every call returns parsed JSON or throws an ApiError
 * with the server's structured message.
 */
import type { ApiErrorBody } from '../types';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let refreshing: Promise<string | null> | null = null;
let onUnauthorized: (() => void) | null = null;

export const setAccessToken = (t: string | null) => { accessToken = t; };
export const getAccessToken = () => accessToken;
export const setOnUnauthorized = (fn: () => void) => { onUnauthorized = fn; };

const BASE = '/api';

async function rawFetch(input: string, init: RequestInit): Promise<Response> {
  return fetch(`${BASE}${input}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
}

/** Exchange the httpOnly refresh cookie for a new access token (once at a time). */
export async function refreshSession(): Promise<string | null> {
  refreshing ??= (async () => {
    try {
      const res = await rawFetch('/auth/refresh', { method: 'POST' });
      if (!res.ok) return null;
      const data = (await res.json()) as { accessToken: string };
      accessToken = data.accessToken;
      return data.accessToken;
    } catch {
      return null;
    } finally {
      // Let the next 401 start a fresh attempt.
      setTimeout(() => { refreshing = null; }, 0);
    }
  })();
  return refreshing;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, unknown>;
  retry?: boolean;
}

function withQuery(path: string, query?: Record<string, unknown>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, query, retry = true, ...init } = opts;

  const send = () =>
    rawFetch(withQuery(path, query), {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

  let res = await send();

  // One transparent retry after a token refresh.
  if (res.status === 401 && retry && !path.startsWith('/auth/')) {
    const token = await refreshSession();
    if (token) res = await send();
  }

  if (res.status === 401) {
    onUnauthorized?.();
    throw new ApiError(401, 'unauthorized', 'Your session has expired. Please sign in again.');
  }

  if (!res.ok) {
    let parsed: ApiErrorBody | null = null;
    try { parsed = (await res.json()) as ApiErrorBody; } catch { /* non-JSON body */ }
    throw new ApiError(
      res.status,
      parsed?.error?.code ?? `http_${res.status}`,
      parsed?.error?.message ?? `Request failed (${res.status})`,
      parsed?.error?.details,
    );
  }

  if (res.status === 204) return undefined as T;
  // Reports can come back as text/csv; only parse JSON when the server says JSON.
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}

export const http = {
  get: <T>(path: string, query?: Record<string, unknown>) => api<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => api<T>(path, { method: 'DELETE' }),
};
